import { Component, Input, Output, EventEmitter } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators, AbstractControl } from '@angular/forms';
import { Mail } from 'src/app/shared/models/mails/mail';
import { ToastrService } from 'ngx-toastr';
import { ConfirmationService } from 'primeng/api';
import * as moment from 'moment';
import { TranslateService } from '@devinforius/i-essentials';
import { formatNumber } from '@angular/common';
import { forkJoin, of } from 'rxjs';
import { IDelibeService } from 'src/app/shared/services/i-delibe.service';
import { ICourrierIDelibeLinks } from 'src/app/shared/models/iDelibe/delibe-links';
import { catchError } from 'rxjs/operators';
import { CourrierLinks, CourrierLinksParams, DelibeLinks } from 'src/app/shared/models/iCourrier/links';
import { Cols } from 'src/app/shared/models/cols';
import { Service } from 'src/app/shared/models/iCourrier/service';
import { ServicesService } from 'src/app/shared/services/services.service';
import { iAdminService } from 'src/app/shared/services/i-admin.service';
import { MailLinkDto, ServiceDto } from 'src/app/shared/interfaces';
import { MailsLinksService } from 'src/app/shared/services/mails-links.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-courrier-lien',
  templateUrl: './courrier-lien.component.html',
  styleUrls: ['./courrier-lien.component.scss'],
})
export class CourrierLienComponent {
  @Input() displayTitle = true;
  @Output() public linkIdEvent = new EventEmitter<number>();
  @Output() public linkCountEvent = new EventEmitter<number>();
  @Input() set mailActive(value: Mail) {
    if (value?.id) {
      this.courrier = value;
      this.getDatas();
    }
  }
  @Input() public dialogId: string;

  public courrier: Mail;

  // Courrier
  public formCourrierLink: UntypedFormGroup = new UntypedFormGroup({
    courrierId: new UntypedFormControl(null),
    linkCourrierId: new UntypedFormControl(null, Validators.required),
    typeLiaison: new UntypedFormControl(null, Validators.required),
  });
  public typesLV: { label: string; value: string }[] = [];
  public links: CourrierLinks[] = [];
  public columns: Cols[] = [];

  public linkMailAnswer: CourrierLinks; // to hide delete btn if current mail  is an answer of this one
  public baseUrl: string;
  public listOfServices: Service[] = [];

  // Delibe
  public hasDelibeAsATenant = false;
  public formDelibeLink: UntypedFormGroup = new UntypedFormGroup({
    linkDelibeId: new UntypedFormControl(null, Validators.required),
  });
  public linksDelibe: DelibeLinks[] = [];
  public columnsDelibe: Cols[] = [];

  constructor(
    private toastr: ToastrService,
    private confirmationService: ConfirmationService,
    public translater: TranslateService,
    private servicesService: ServicesService,
    private iDelibeService: IDelibeService,
    private iAdminService: iAdminService,
    private mailsLinksService: MailsLinksService
  ) {
    const config = environment;
    this.baseUrl = config.baseUrl;
  }

  get f(): { [key: string]: AbstractControl } {
    return this.formCourrierLink.controls;
  }

  getDatas(): void {
    this.getTranslations();
    this.getGlobalTypes();
    this.getTenantInfos();
  }

  getTranslations(): void {
    this.columns = [
      {
        field: 'idFormatted',
        header: this.translater.translate('courrierLink.mailId'),
      },
      {
        field: 'objet',
        header: this.translater.translate('courrierLink.subject'),
      },
      {
        field: 'typeLiaison',
        header: this.translater.translate('courrierLink.relationshipType'),
      },
      {
        field: 'serviceProprietaireLabel',
        header: this.translater.translate('courrierLink.service'),
      },
    ];
    this.columnsDelibe = [
      {
        field: 'idFormatted',
        header: this.translater.translate('courrierLink.delibeId'),
      },
      {
        field: 'objet',
        header: this.translater.translate('courrierLink.subject'),
      },
      {
        field: 'typeLiaison',
        header: this.translater.translate('courrierLink.relationshipType'),
        format: (value) => {
          return value;
        },
      },
      {
        field: 'serviceProprietaireLabel',
        header: this.translater.translate('courrierLink.service'),
      },
    ];
  }

  getTenantInfos(): void {
    this.iAdminService.getTenant().subscribe({
      next: (tenantsInfos) => {
        if (tenantsInfos?.applications.find((app) => app.name === 'iDélibé Backoffice')) {
          this.hasDelibeAsATenant = true;
        }
        this.getServices();
      },
      error: (error: Error) => {
        this.toastr.error(this.translater.translate('error.noTenantRetrieved'));
        console.error('Error : ', error);
      },
    });
  }
  getServices(): void {
    this.servicesService.fetchServices().subscribe({
      next: (res: ServiceDto[]) => {
        this.listOfServices = [];
        res.map((service) => {
          this.listOfServices.push(new Service(service));
        });
        this.getLinks(this.courrier.id);
      },
    });
  }

  getLinks(courrierID: number): void {
    forkJoin({
      courrierLinks: this.mailsLinksService.fetchMailLinks(courrierID),
      delibeLinks: this.hasDelibeAsATenant ? this.mailsLinksService.getIDelibeLinks(courrierID) : of({ delibeLiens: [] }),
    }).subscribe({
      next: (forkJoinResponse) => {
        this.formatCourrierLinks(courrierID, forkJoinResponse.courrierLinks);
        if (this.hasDelibeAsATenant) {
          this.formatDelibeLinks(forkJoinResponse.delibeLinks.delibeLiens);
        } else {
          this.emitLinkCount();
        }
      },
    });
  }

  emitLinkCount(): void {
    this.linkCountEvent.emit(this.links.length + this.linksDelibe.length);
  }

  formatCourrierLinks(courrierID: number, response: MailLinkDto[]): void {
    this.links = [];

    response.map((item) => {
      let dataToPushInList = null;
      //on va vérifier si le linkCourrierId est bien différent du courrierId afin de ne pas afficher la même chose
      if (+courrierID !== +item.linkCourrierId) {
        dataToPushInList = item.linkCourrier;
      } else {
        dataToPushInList = item.courrier;
      }

      //puis on va setter le reste des données;
      dataToPushInList.courrierId = item.courrierId;
      dataToPushInList.linkCourrierId = item.linkCourrierId;

      //puis on va récupérer et formatter les autres données
      dataToPushInList.typeLiaison = item.typeLiaison;
      dataToPushInList.idFormatted = formatNumber(dataToPushInList.id, 'es');
      if (dataToPushInList.serviceProprietaireId) {
        dataToPushInList.serviceProprietaireLabel = this.listOfServices.find((service) => {
          return +service.id === +dataToPushInList.serviceProprietaireId;
        })?.description;
      }

      this.links.push(dataToPushInList);
    });

    this.links = this.links.sort((a, b) => (moment(a.dateDocument) < moment(b.dateDocument) ? 1 : -1));

    // check if its an answer or not
    const linkAnswer = this.links.find((a) => a.courrierId === this.courrier.id && a.typeLiaison === 'Réponse');
    if (linkAnswer && linkAnswer !== undefined) {
      this.linkIdEvent.emit(linkAnswer.linkCourrierId);
      this.linkMailAnswer = linkAnswer;
    }
  }

  formatDelibeLinks(listOfDelibeLiens): void {
    //formatting the response of links delibe to fit inside the table
    this.linksDelibe = [];
    if (listOfDelibeLiens.length > 0) {
      const listOfPointIds = [];
      listOfDelibeLiens.forEach((delibe) => {
        listOfPointIds.push(delibe.pointId);
      });
      this.iDelibeService.getPointsInfos(listOfPointIds).subscribe({
        next: (listOfPoints) => {
          listOfPoints.forEach((point) => {
            const delibeLink = listOfDelibeLiens.find((delibeLink) => delibeLink.pointId === point.id);
            this.linksDelibe.push({
              delibeLinkId: delibeLink.id,
              pointId: point.id,
              idFormatted: formatNumber(point.id, 'es'),
              objet: point.objetSynthese,
              typeLiaison: this.translater.translate('courrierFiche.deliberation'),
              serviceProprietaireLabel: point.serviceDescription,
            });
          });
          this.linksDelibe = [...this.linksDelibe];
        },
        error: () => {
          this.toastr.error(this.translater.translate('error.errorOccurredWhileFetchingPointsInfos'));
        },
      });
    }
  }

  getGlobalTypes(): void {
    this.mailsLinksService.getGlobalTypes().subscribe({
      next: (res) => {
        this.typesLV = [];
        if (res.length > 0) {
          res.map((item) => {
            this.typesLV.push({ label: item.description, value: item.description });
          });
        }
      },
    });
  }

  submitLink(): void {
    this.formCourrierLink.value.courrierId = this.courrier.id;
    this.mailsLinksService.postLinks(this.formCourrierLink.value).subscribe({
      next: () => {
        this.toastr.success(this.translater.translate('thankYou'), this.translater.translate('courrierLink.linkAdded'));
        this.formCourrierLink.reset();
        this.getLinks(this.courrier.id);
      },
    });
  }
  submitLinkDelibe(): void {
    const dataForICourrierQuery: ICourrierIDelibeLinks = {
      type: 'deliberation',
      pointId: this.formDelibeLink.value.linkDelibeId,
    };

    // D'abord on call iDelibe. Si la requête passe, alors on peut call la requête de iCourrier
    // En effet, la requête d'iDélibé va vérifier l’existence,... du point ALORS que la requête iCourrier va simplement ajouter le point de Délibé en tant que string.
    this.iDelibeService.postLinkCourrierIdDelibeId([this.courrier.id], this.formDelibeLink.value.linkDelibeId).subscribe({
      next: () => {
        this.mailsLinksService.postLinkCourrierIdDelibeId(this.courrier.id, dataForICourrierQuery).subscribe({
          next: () => {
            this.toastr.success(this.translater.translate('thankYou'), this.translater.translate('courrierLink.linkAdded'));
            this.formDelibeLink.reset();
            this.getLinks(this.courrier.id);
          },
        });
      },
      error: () => {
        this.toastr.error(this.translater.translate('error.pointNotFoundOrNoAccessToPoint'));
      },
    });
  }

  removeLink(item): void {
    const data: CourrierLinksParams = {
      linkCourrierId: item.linkCourrierId,
      courrierId: item.courrierId,
      typeLiaison: item.typeLiaison,
    };
    this.confirmationService.confirm({
      message: this.translater.translate('courrierLink.confirmDeleteLink'),
      accept: () => {
        this.mailsLinksService.deleteLinks(data).subscribe({
          next: () => {
            this.toastr.success(this.translater.translate('courrierLink.linkDeleted'));
            this.getLinks(this.courrier.id);
          },
        });
      },
      key: this.dialogId,
    });
  }

  removeLinkDelibe(item): void {
    this.confirmationService.confirm({
      message: this.translater.translate('courrierLink.confirmDeleteLink'),
      accept: () => {
        forkJoin({
          deleteDelibeLinks: this.mailsLinksService.deleteIDelibeLinks(this.courrier.id, item.delibeLinkId).pipe(
            catchError(() => {
              this.toastr.error(this.translater.translate('error.errorWhileDeletingLink'));
              return of({});
            })
          ),
          deleteConnectorDelibeLinks: this.iDelibeService.deleteIDelibeLinks(this.courrier.id, item.pointId).pipe(
            catchError(() => {
              this.toastr.error(this.translater.translate('error.errorWhileDeletingLink'));
              return of({});
            })
          ),
        }).subscribe({
          next: () => {
            this.toastr.success(this.translater.translate('courrierLink.linkDeleted'));
            this.getLinks(this.courrier.id);
          },
        });
      },
      key: this.dialogId,
    });
  }

  onRowSelect(event): void {
    const url = '/private/ic/tabs/';
    window.open(this.baseUrl + url + event.id);
  }

  public onRowSelectiDelibePoint(event): void {
    window.open(`${environment.iDelibeUrl}/private/points/detail/${event.pointId}`, '_blank');
  }
}
